// The results body for a scoring v4 take, built from the same pieces as v1, with v4's own
// content: nine gifts with scores out of 100, the office-pattern sentence and the person's own
// recent-memory answers quoted back. It has no codex, and never shows the confidence or flags:
// those are for staff. The results PDF is this body printed (screens/ResultPrint, `print`).
//
// The personalized write-up is spread across the sections (WriteupParts): everything scored
// shows at once, and each written part fills in beside it.
//   Hero, then the bar while it's written
//   01 Where you are
//   02 How you're built          + "How you're built"
//   03 Your top gifts            + "How your gifts are showing up right now"
//   04 How you connect with others (only with a write-up)
//   05 What this means and how to use it: the profile card, the theme, the quotes,
//      + "What's in the way", first move, blind spot, + "Your next step", + anything else written

import type { ReactNode } from "react";
import { BLIND_SPOT_TIP, DOMAIN_MEANINGS, FREEDOM_THEMES, OFFICE_TIP, QUADRANT_TIPS, THEME_INTRO, domainMeaningAfterName, domainTip } from "../lib/explainers";
import { GIFT_EXPLAINERS } from "../lib/giftExplainers";
import { domainColor } from "../lib/domainColors";
import { giftColor } from "../lib/giftColors";
import { Diamond } from "./Diamond";
import type { DomainCode, ResultViewV4 } from "../lib/types";
import { Thumbs, useWriteup } from "./PersonalWriteup";
import type { MapRecord } from "./ResultMap";
import { FreedomClarityBody, ResultHero, ResultSection } from "./ResultParts";
import { Term } from "./Term";
import { PairingBody } from "./Pairing";
import { pairingFor } from "@giftsprofile/scoring/pairing";
import { ConnectPart, MeaningPanel, WRITTEN_BY_AI, WriteupLeftovers, WriteupSlot, WriteupStatus, writeupState } from "./WriteupParts";
import { SendRatersNudge } from "./SendRatersNudge";
import { WhatOthersSee } from "./WhatOthersSee";

export function ResultBodyV4({
  result,
  resultKey,
  emailEnabled = false,
  readOnly: readOnlyProp = false,
  print = false,
  top,
}: {
  result: ResultViewV4;
  resultKey?: string | null;
  /** Results emails go out, so copy may point people to them. */
  emailEnabled?: boolean;
  /** The admin's copy: the write-up without asking again or thumbs. */
  readOnly?: boolean;
  /**
   * The results PDF (screens/ResultPrint): read-only, with the top gifts opened, since paper
   * can't be tapped. What only makes sense on a screen is hidden by `print:hidden`.
   */
  print?: boolean;
  /** Under the hero, before anything else (the results page opened from its email: SeeAllProfilesCard). */
  top?: ReactNode;
}) {
  const readOnly = readOnlyProp || print;
  const { record, display } = result;
  const domainNames = Object.fromEntries(display.domains.map((d) => [d.code, d.name])) as Record<DomainCode, string>;
  const home = display.domains.find((d) => d.code === record.primary_domain);
  const homeName = home?.name ?? record.primary_domain;
  const scenarios = Math.max(1, display.scenarioCount);
  const isHome = (code: DomainCode) => code === record.primary_domain;
  // Most picks first; on a tie the home base leads (it won the tie).
  const domainsByCount = [...display.domains].sort((a, b) => b.count - a.count || Number(isHome(b.code)) - Number(isHome(a.code)));
  const giftsByRank = [...record.gifts].sort((a, b) => a.rank - b.rank);
  const scoreOf = (gift: string): number | null => record.gifts.find((g) => g.gift === gift)?.score ?? null;
  const lineOf = (gift: string): string => display.gifts.find((g) => g.code === gift)?.line ?? "";
  const work = record.work_on.mode === "work";
  const theme = display.theme;
  const topGift = record.top_gifts[0];
  const { view, gaveUp } = useWriteup({ takeId: result.takeId, writeup: result.writeup, resultKey, readOnly });
  const written = writeupState(view, readOnly);
  // "How you connect with others" is all written: its section shows only with a write-up coming
  // or there, and the sections after it are numbered to match.
  const connect = written.kind === "pending" || (written.kind === "ready" && written.part("connect") !== null);
  // The Body at Work: their pairing (top two gifts) on the nine-gift cycle (v4.1 doc).
  const pairing = pairingFor(record);
  const connectN = 5;
  const meaningN = connect ? 6 : 5;
  // What others see (the v3.1 rater test): last, after what to work on. None without rater links.
  const othersN = meaningN + 1;
  // v4 has a home base but no second domain on the map: pointing "second" at the home base keeps
  // just that one arc highlighted.
  const mapRecord: MapRecord = {
    freedom: record.freedom,
    clarity: record.clarity,
    quadrant: record.quadrant,
    borderline: false,
    balanced: false,
    primary_domain: record.primary_domain,
    secondary_domain: record.primary_domain,
    domain_counts: record.domain_counts,
  };

  return (
    <>
      {/* Hero */}
      <ResultHero
        name={display.profile.name}
        quadrant={record.quadrant}
        domainName={homeName}
        topGift={topGift}
        marks={{ domain: domainColor(record.primary_domain).fill, gift: topGift ? giftColor(topGift).fill : undefined }}
        // The three chips said in one sentence, in Taylor's words (Edward, Oct 22: "a synopsis at
        // the top"), so the chips need no tap-to-explain.
        summary={
          <>
            Right now you're <strong>{record.quadrant}</strong>. {QUADRANT_TIPS[record.quadrant]} You lead with <strong>{homeName}</strong>: you{" "}
            {DOMAIN_MEANINGS[record.primary_domain].first}.
            {topGift && GIFT_EXPLAINERS[topGift] ? (
              <>
                {" "}
                Your top gift is <strong>{topGift}</strong>: {lowerFirst(GIFT_EXPLAINERS[topGift].healthy)}
              </>
            ) : null}
          </>
        }
      />

      {print ? null : top}

      {/* The bar while the write-up is written (about 25 seconds), right where they land. */}
      <WriteupStatus view={view} gaveUp={gaveUp} readOnly={readOnly} />

      {/* Send the three rater links now (Taylor: "so they don't miss it"). */}
      {readOnly ? null : <SendRatersNudge observer={result.observer} takeId={result.takeId} resultKey={resultKey} />}

      {/* 01 */}
      <ResultSection n={1} label="Where you are" title="Freedom × Clarity">
        <FreedomClarityBody
          record={mapRecord}
          domainNames={domainNames}
          series={result.series}
          history={result.history}
          emailEnabled={emailEnabled}
          takeId={print ? undefined : result.takeId}
        />
      </ResultSection>

      {/* 02 */}
      <ResultSection n={2} label="How you're built" title={`${homeName}${home?.line ? ` · ${home.line}` : ""}`}>
        {/* What their domain means, word for word what its tooltip says (the title already names it). */}
        <p className="text-[17px] leading-snug">
          <strong>{DOMAIN_MEANINGS[record.primary_domain].name}</strong> {domainMeaningAfterName(record.primary_domain)}
        </p>
        <ul className="mt-5 flex flex-col gap-3" aria-label="Your domains">
          {domainsByCount.map((d) => (
            <li key={d.code}>
              <div className="flex items-center gap-2 text-[15px]">
                <Diamond color={domainColor(d.code).fill} />
                <Term tip={domainTip(d.code)} className={isHome(d.code) ? "font-bold" : ""}>
                  {d.name}
                </Term>
              </div>
              <div className="mt-1.5 h-2.5 w-full rounded-full bg-soft" aria-hidden="true">
                {/* The home base in its own domain colour, as on the map and the homepage; the rest grey. */}
                <div
                  className={`h-full rounded-full ${isHome(d.code) ? "" : "bg-muted/40"}`}
                  style={{ width: `${Math.min(100, (d.count / scenarios) * 100)}%`, ...(isHome(d.code) ? { backgroundColor: domainColor(d.code).fill } : {}) }}
                />
              </div>
            </li>
          ))}
        </ul>
        <WriteupSlot state={written} place="built" />
      </ResultSection>

      {/* 03 */}
      <ResultSection n={3} label="Your top gifts" title={record.top_gifts.join(", ")}>
        <ol className="flex flex-col gap-3">
          {record.top_gifts.map((gift, i) => {
            // Taylor's own one-liner when the wording has one; otherwise her healthy sentence.
            const line = lineOf(gift) || GIFT_EXPLAINERS[gift]?.healthy || "";
            const score = scoreOf(gift);
            const explainer = GIFT_EXPLAINERS[gift];
            // Tinted in the gift's colour, like the homepage's gift card.
            const fill = giftColor(gift).fill;
            return (
              <li
                key={gift}
                className="rounded-[16px] border"
                style={{ borderColor: `color-mix(in srgb, ${fill} 70%, transparent)`, backgroundColor: `color-mix(in srgb, ${fill} 10%, var(--color-panel))` }}
              >
                <details className="group" data-testid={`gift-${gift}`} open={print || undefined}>
                  <summary className="flex cursor-pointer list-none items-start gap-4 p-4 [&::-webkit-details-marker]:hidden">
                    <span className="numeral text-[34px] text-accent-text" aria-hidden="true">
                      {i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <h3 className="heading flex items-center gap-2.5 text-[22px] leading-tight">
                        <span className="sr-only">{i + 1}. </span>
                        <Diamond color={fill} />
                        {gift}
                      </h3>
                      {line ? <p className="mt-1 text-[15px] leading-snug text-muted">{line}</p> : null}
                      {explainer ? (
                        <span className="mt-2 inline-block text-[14px] font-semibold text-accent-text print:hidden">
                          <span className="group-open:hidden">More about {gift}</span>
                          <span className="hidden group-open:inline">Less</span>
                        </span>
                      ) : null}
                    </div>
                    {score !== null ? (
                      <span className="numeral shrink-0 text-[24px]" aria-label={`Score ${score}`}>
                        {score}
                      </span>
                    ) : null}
                  </summary>
                  {explainer ? (
                    <GiftMeaning gift={gift} home={record.primary_domain} homeName={homeName} withHealthy={false} className="mx-4 mb-4 border-t border-line pt-3" />
                  ) : null}
                </details>
              </li>
            );
          })}
        </ol>
        {/* All nine with their scores, open from the start (Oct 21 call: like StrengthsFinder, show
            the bottom ones too, so people see a gift they misuse rather than one they lack). */}
        <details open className="mt-4 rounded-[16px] border border-line bg-panel" data-testid="all-gifts">
          <summary className="flex min-h-12 cursor-pointer items-center px-4 font-semibold">All {giftsByRank.length} gifts</summary>
          <ol className="border-t border-line px-4 py-2">
            {giftsByRank.map((g) => (
              <li key={g.gift} className="flex min-h-11 items-center justify-between gap-3 text-[15px]" data-testid={`all-gift-${g.gift}`}>
                <span className="flex items-center">
                  <span className="inline-block w-7 text-muted">{g.rank}.</span>
                  <Diamond color={giftColor(g.gift).fill} className="mr-2.5" />
                  <GiftTerm gift={g.gift} home={record.primary_domain} homeName={homeName} />
                </span>
                <span className="text-muted tabular-nums">{g.score}/100</span>
              </li>
            ))}
          </ol>
        </details>
        {/* The diamonds' key, as under the homepage's gifts. */}
        <p className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-muted" data-testid="gift-legend">
          {(["STR", "REL", "EXE", "INF"] as DomainCode[]).map((c) => (
            <span key={c} className="inline-flex items-center gap-1.5">
              <Diamond color={domainColor(c).fill} className="h-2 w-2" />
              {domainNames[c] ?? DOMAIN_MEANINGS[c].name}
            </span>
          ))}
        </p>
        <p className="mt-4 text-[14px] leading-snug text-muted">{display.giftsNote}</p>
        {display.officeSentence ? (
          <p className="mt-4 text-[17px] leading-snug">
            {display.officeSentence}{" "}
            <Term tip={OFFICE_TIP} className="text-[15px] text-accent-text print:hidden">
              What does this mean?
            </Term>
          </p>
        ) : null}
        <WriteupSlot state={written} place="gifts" />
      </ResultSection>

      {/* 04: their pairing on the Body at Work cycle */}
      <ResultSection n={4} label="How you work" title={pairing.name}>
        <PairingBody pairing={pairing} />
      </ResultSection>

      {/* 05: written for them, so only with a write-up */}
      {connect ? (
        <ResultSection n={connectN} label={WRITTEN_BY_AI} title="How you connect with others">
          <ConnectPart state={written} />
        </ResultSection>
      ) : null}

      {/* 05 (04 without a write-up): the profile card and what to work on, together */}
      <ResultSection n={meaningN} label="What this means and how to use it" title={theme.name}>
        <MeaningPanel>{display.profile.youCore || display.profile.core}</MeaningPanel>
        <p className="mt-6 text-[17px] leading-snug">{work ? theme.meaning : display.guardLine}</p>
        <p className="mt-2 text-[15px] print:hidden">
          <Term
            className="text-accent-text"
            tip={
              <>
                {THEME_INTRO}
                {FREEDOM_THEMES.map((t) => (
                  <span key={t.code} className="mt-2 block">
                    <strong>{t.name}:</strong> {t.meaning}
                  </span>
                ))}
              </>
            }
          >
            What are the freedom themes?
          </Term>
        </p>
        {display.quotes.length > 0 ? (
          <ul className="mt-5 flex flex-col gap-3" data-testid="quotes">
            {display.quotes.map((q) => (
              <li key={q.id} className="border-l-2 border-accent pl-3 text-[17px] leading-snug">
                {`${q.label}: "${q.text}"`}
              </li>
            ))}
          </ul>
        ) : null}
        <WriteupSlot state={written} place="way" />
        {/* Only with raters; if the page has no rater section, it reads here rather than nowhere. */}
        {result.observer ? null : <WriteupSlot state={written} place="others" />}
        {work ? (
          <p className="mt-5 text-[17px] leading-snug">
            <strong>First move:</strong> {theme.firstMove}
          </p>
        ) : null}
        <p className="mt-4 text-[17px] leading-snug">
          <Term tip={BLIND_SPOT_TIP}>
            <strong>Blind spot:</strong>
          </Term>{" "}
          {display.blindSpot.line || display.blindSpot.name}
        </p>
        <WriteupSlot state={written} place="next" />
        <WriteupLeftovers state={written} />
      </ResultSection>

      {/* What others see: the three links until two people finish, then the combined view */}
      {result.observer ? (
        <ResultSection n={othersN} label="What others see" title="How the people who know you see you">
          <WhatOthersSee
            takeId={result.takeId}
            observer={result.observer}
            resultKey={resultKey}
            readOnly={readOnly}
            print={print}
            selfGifts={record.gifts}
            selfDomains={record.domain_counts}
          />
          <WriteupSlot state={written} place="others" />
        </ResultSection>
      ) : null}

      {written.kind === "ready" && !readOnly && view ? (
        <Thumbs takeId={result.takeId} resultKey={resultKey} vote={view.feedback ?? null} />
      ) : null}

      {/* No "Scoring v4 · wording 4.1" line: people never need it (Edward, Oct 26). Staff see versions in the admin. */}
    </>
  );
}

/**
 * What a gift means, from Taylor's Oct 21 texts and his words on the call ("there's the healthy
 * gift and there's the weaponized gift and putting both of them there"): the healthy form, the
 * weaponized form, and how it looks through the person's home base. `withHealthy` is off only
 * where the healthy line is already showing right above it (the top-three cards).
 */
function GiftMeaning({
  gift,
  home,
  homeName,
  withHealthy = true,
  className = "",
}: {
  gift: string;
  home: DomainCode;
  homeName: string;
  withHealthy?: boolean;
  className?: string;
}) {
  const explainer = GIFT_EXPLAINERS[gift];
  if (!explainer) return null;
  return (
    <dl className={`flex flex-col gap-3 text-[15px] leading-snug ${className}`}>
      {withHealthy ? (
        <div>
          <dt className="font-semibold">Healthy</dt>
          <dd className="mt-0.5 text-muted">{explainer.healthy}</dd>
        </div>
      ) : null}
      <div>
        <dt className="font-semibold">Weaponized (when freedom is low)</dt>
        <dd className="mt-0.5 text-muted">{explainer.weaponized}</dd>
      </div>
      <div>
        <dt className="font-semibold">Through {homeName}, your home base</dt>
        <dd className="mt-0.5 text-muted">{explainer.byDomain[home]}.</dd>
      </div>
    </dl>
  );
}

/** The same three lines as a tap/hover note on a gift's name. */
function GiftTerm({ gift, home, homeName }: { gift: string; home: DomainCode; homeName: string }) {
  if (!GIFT_EXPLAINERS[gift]) return <>{gift}</>;
  return (
    <Term tip={<GiftMeaning gift={gift} home={home} homeName={homeName} />}>
      {gift}
    </Term>
  );
}

/** "You turn confusing ideas…" → "you turn confusing ideas…", to follow a colon mid-sentence. */
const lowerFirst = (text: string) => text.charAt(0).toLowerCase() + text.slice(1);
